/**
 * Single-question and bulk review actions (Accept / Modify / Reject / Reset)
 * with optimistic store updates and API persistence.
 *
 * Each action updates the session store immediately, then fires a background
 * API call. `resetQuestion` is local-only and is never sent to the API.
 */

import { useCallback } from 'react';
import { useSessionStore } from '@/stores/useSessionStore';
import { useReviewStore } from '@/stores/useReviewStore';
import { useUpdateResponseAction } from '@/hooks';
import { useToast } from '@/components/common';
import type { DdqSessionQuestion, UserActionType } from '@/types/api.types';

/**
 * Return shape for {@link useReviewActions}.
 *
 * `acceptAboveThreshold` and `acceptSelected` operate on multiple questions
 * at once; all others act on a single question by ID.
 * `resetQuestion` reverts a question to Pending locally only — no API call.
 */
export interface UseReviewActionsReturn {
  acceptQuestion: (questionId: string) => void;
  modifyQuestion: (questionId: string, newResponse: string) => void;
  rejectQuestion: (questionId: string) => void;
  acceptSelected: () => void;
  acceptAboveThreshold: (threshold: number, questions: DdqSessionQuestion[]) => number;
  resetQuestion: (questionId: string) => void;
  isPending: boolean;
}

/**
 * Provides Accept, Modify, Reject, and Reset actions for DDQ session responses.
 *
 * Binds to the current session via `useSessionStore.currentSessionId`.
 * All mutating actions apply an optimistic store update before the API call so
 * the UI reflects the change without waiting for the server round-trip.
 *
 * `acceptAboveThreshold` accepts all pending questions whose `confidence`
 * is greater than or equal to `threshold` and returns the count of questions
 * acted on.
 *
 * `acceptSelected` accepts all currently selected pending questions and clears
 * the selection.
 *
 * `resetQuestion` reverts a question's `userAction` to Pending in the local
 * store only; the change is not sent to the API.
 *
 * @returns Action callbacks and `isPending` reflecting the underlying mutation state.
 */
export function useReviewActions(): UseReviewActionsReturn {
  const updateQuestion = useSessionStore((s) => s.updateQuestion);
  const questions = useSessionStore((s) => s.questions);
  const currentSessionId = useSessionStore((s) => s.currentSessionId);
  const { selectedQuestionIds, clearSelection, cancelEditing } = useReviewStore();
  const { success: showSuccess } = useToast();

  // API mutation for persisting actions
  const mutation = useUpdateResponseAction(currentSessionId ?? '');

  // Helper to persist action to API
  const persistAction = useCallback(
    (questionId: string, action: UserActionType, modifiedAnswer?: string) => {
      if (currentSessionId) {
        mutation.mutate({
          responseId: questionId,
          request: {
            action,
            modified_answer: modifiedAnswer,
          },
        });
      }
    },
    [currentSessionId, mutation]
  );

  const acceptQuestion = useCallback(
    (questionId: string) => {
      // Optimistic update
      updateQuestion(questionId, {
        userAction: 'Accepted',
        modifiedDate: new Date().toISOString(),
      });
      cancelEditing();
      // Persist to API
      persistAction(questionId, 'Accept');
      showSuccess('Answer accepted');
    },
    [updateQuestion, cancelEditing, persistAction, showSuccess]
  );

  const modifyQuestion = useCallback(
    (questionId: string, newResponse: string) => {
      // Optimistic update
      updateQuestion(questionId, {
        userAction: 'Modified',
        userResponse: newResponse,
        modifiedDate: new Date().toISOString(),
      });
      cancelEditing();
      // Persist to API
      persistAction(questionId, 'Modify', newResponse);
      showSuccess('Answer updated');
    },
    [updateQuestion, cancelEditing, persistAction, showSuccess]
  );

  const rejectQuestion = useCallback(
    (questionId: string) => {
      // Optimistic update
      updateQuestion(questionId, {
        userAction: 'Rejected',
        modifiedDate: new Date().toISOString(),
      });
      cancelEditing();
      // Persist to API
      persistAction(questionId, 'Reject');
      showSuccess('Question skipped');
    },
    [updateQuestion, cancelEditing, persistAction, showSuccess]
  );

  const acceptSelected = useCallback(() => {
    const now = new Date().toISOString();
    selectedQuestionIds.forEach((questionId) => {
      const question = questions.find((q) => q.question_id === questionId);
      if (question && (!question.userAction || question.userAction === 'Pending')) {
        // Optimistic update
        updateQuestion(questionId, {
          userAction: 'Accepted',
          modifiedDate: now,
        });
        // Persist to API
        persistAction(questionId, 'Accept');
      }
    });
    clearSelection();
  }, [selectedQuestionIds, questions, updateQuestion, clearSelection, persistAction]);

  const acceptAboveThreshold = useCallback(
    (threshold: number, questionsToCheck: DdqSessionQuestion[]) => {
      const now = new Date().toISOString();
      let count = 0;

      questionsToCheck.forEach((question) => {
        if (
          question.confidence >= threshold &&
          (!question.userAction || question.userAction === 'Pending')
        ) {
          // Optimistic update
          updateQuestion(question.question_id, {
            userAction: 'Accepted',
            modifiedDate: now,
          });
          // Persist to API
          persistAction(question.question_id, 'Accept');
          count++;
        }
      });

      return count;
    },
    [updateQuestion, persistAction]
  );

  const resetQuestion = useCallback(
    (questionId: string) => {
      updateQuestion(questionId, {
        userAction: 'Pending',
        userResponse: undefined,
        modifiedDate: undefined,
      });
      // Note: Reset is not persisted to API - it's a local-only action
    },
    [updateQuestion]
  );

  return {
    acceptQuestion,
    modifyQuestion,
    rejectQuestion,
    acceptSelected,
    acceptAboveThreshold,
    resetQuestion,
    isPending: mutation.isPending,
  };
}
